import { z } from "zod"

import { TenantIdSchema, UserIdSchema } from "./auth.js"
import {
	CarrierIdSchema,
	CountryCodeSchema,
	DocumentIdSchema,
	LaneIdSchema,
	LotIdSchema,
	PartyIdSchema,
	ShipmentIdSchema,
	UnlocodeSchema,
	VerticalIdSchema,
} from "./id-schemas.js"

/**
 * Shipment — top-level transit unit.
 *
 * Owns Lots, Parties (4+ deep notify chains), and Documents; references
 * Carrier + Lane. Status tracks the lifecycle from booking through
 * delivery. External agents (forwarders, brokers) are explicit so per-
 * shipment access can be gated without role changes.
 */

const Bounded = (max = 256) => z.string().min(1).max(max)
const IsoDateTime = z.string().datetime()

export const ShipmentStatusSchema = z.enum([
	"DRAFT",
	"BOOKED",
	"IN_TRANSIT",
	"ARRIVED",
	"DELIVERED",
	"CANCELLED",
])
export type ShipmentStatus = z.infer<typeof ShipmentStatusSchema>

export const ShipmentSchema = z.object({
	id: ShipmentIdSchema,
	tenantId: TenantIdSchema,
	carrierId: CarrierIdSchema,
	// Joined carrier display name surfaced by the read endpoints. Nullable
	// (left join) + optional (not all producers populate it).
	carrierName: Bounded(256).nullable().optional(),
	laneId: LaneIdSchema,
	status: ShipmentStatusSchema,
	// App-wide scope pair (mirrors LotSchema): vertical + origin country with
	// joined display names surfaced by the read endpoints (names-not-UUIDs).
	verticalId: VerticalIdSchema,
	// Joined vertical display name (left join, optional) — names-not-UUIDs.
	verticalName: Bounded(128).nullable().optional(),
	originCountry: CountryCodeSchema,
	// Joined origin-country display name (resolved from the countries reference).
	countryName: Bounded(128).nullable().optional(),
	bookingNumber: Bounded(64).optional(),
	blNumber: Bounded(64).optional(),
	originPort: UnlocodeSchema.optional(),
	destinationPort: UnlocodeSchema.optional(),
	vesselName: Bounded(128).optional(),
	voyageNumber: Bounded(32).optional(),
	etd: IsoDateTime.optional(),
	eta: IsoDateTime.optional(),
	freeTimeDays: z.number().int().nonnegative().optional(),
	lotIds: z.array(LotIdSchema),
	partyIds: z.array(PartyIdSchema),
	documentIds: z.array(DocumentIdSchema),
	externalAgentUserIds: z.array(UserIdSchema),
	createdAt: IsoDateTime,
	updatedAt: IsoDateTime,
})
export type Shipment = z.infer<typeof ShipmentSchema>
